// imports
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button } from '@mui/material';
import { CardNumberElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { FC } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';

// components
import StripeCheckoutForm from 'components/Auth/StripeCheckoutForm';
import { Alert } from 'components/common/Alert';

// interfaces, constants, helper
import { LoadingButton } from 'components/common/LoadingButton';
import { ADD_TEXT, ADDING_NEW_CARD_TEXT, CANCEL_TEXT, STRIPE_NOT_LOADED_TEXT } from 'constants/constant';
import { stripeFormInitialValues } from 'constants/formInitialValues';
import { HTTP_STATUS } from 'constants/index';
import { useAddPaymentMethodMutation } from 'generated/graphql';
import { AddCardFormType, StripeFormType } from 'interfaces';
import { flexCenterJustifyEnd } from 'theme/styleConstant';
import { stripePaymentSchema } from 'validationSchema';

const AddCardForm: FC<AddCardFormType> = ({ setFormLoading, formLoading, onComplete, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();

  const methods = useForm<StripeFormType>({
    defaultValues: stripeFormInitialValues,
    resolver: yupResolver(stripePaymentSchema),
  });

  const [addPaymentMethod] = useAddPaymentMethodMutation({
    onCompleted: async (data) => {
      const { addPaymentMethod } = data;
      const { response, clientSecret } = addPaymentMethod || {};
      const { status, message } = response || {};

      if (status !== HTTP_STATUS.SUCCESS) {
        Alert.error(message ?? '');
      } else if (clientSecret) {
        const confirmPayment = await stripe?.confirmCardSetup(clientSecret);
        const { error } = confirmPayment || {};
        if (error) {
          const { message } = error;
          Alert.error(message ?? '');
          setFormLoading(false);
          methods.reset();
          return;
        }
      } else {
        Alert.success(message || '');
      }

      setFormLoading(false);
      methods.reset();

      if (onComplete) {
        onComplete();
      }
    },
    onError: (error) => {
      setFormLoading(false);
      Alert.error(error.message);
      methods.reset();
    },
  });

  const onSubmit: SubmitHandler<StripeFormType> = async (data) => {
    const { name } = data;
    if (!stripe) {
      Alert.error(STRIPE_NOT_LOADED_TEXT);
      return;
    }
    if (!elements?.getElement) {
      Alert.error(STRIPE_NOT_LOADED_TEXT);
      return;
    }

    setFormLoading(true);
    const response = await stripe.createPaymentMethod({
      type: 'card',
      card: elements.getElement(CardNumberElement)!,
      billing_details: {
        name,
      },
    });

    const { error, paymentMethod } = response || {};

    if (error) {
      Alert.error(error.message || '');
      setFormLoading(false);
    } else {
      const { id } = paymentMethod || {};
      await addPaymentMethod({
        variables: {
          addPaymentMethodInput: {
            paymentMethodId: id ?? '',
          },
        },
      });
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <StripeCheckoutForm />
        <Box {...flexCenterJustifyEnd} pt={2} columnGap={2}>
          <Button variant="outlined" disabled={formLoading} onClick={onCancel}>
            {CANCEL_TEXT}
          </Button>
          <LoadingButton loading={formLoading} text={ADD_TEXT} loadingText={ADDING_NEW_CARD_TEXT} appLoader />
        </Box>
      </form>
    </FormProvider>
  );
};

export default AddCardForm;
